/* ------------------------------------------------------------------------------------------------------------------
   SIZE GUIDE
   Country-conversion matrix: hover shades a column, clicking a size collapses the matrix to the row
   that was clicked ("Remove selected size" clears it), reveals garment measurements (with a
   cm/inches switch) and an "Add to cart" button for that size.

   The size added to cart is remembered per sizing family (data-family on the guide root, see
   snippets/size-family.liquid): the next guide of that family opens already collapsed to that row
   with that size selected, and snippets/size-recommendation.liquid shows "Size S recommended
   (36 FR)" in place of Kleep's "Find your size". The stored entry carries the value of every size
   system in the chosen column, so a size picked on one matrix resolves on any other of the same
   family whatever row it keeps (jeans have no INT row, coats head with FR...).

   Lifted out of an inline <script> in snippets/size_table.liquid so that guides added after page
   load get wired up too: the shop-the-look size panels fetch the look product's own guide on
   demand, and markup injected through innerHTML never runs its own <script> tags.

   Exposed as window.initSizeGuides(root) — safe to call repeatedly, already-wired guides are
   skipped. Existing guides on the page are initialised automatically. The memory helpers are
   exposed as window.SizeGuideMemory for component-size-recommendation.js.

   Wrapped in an IIFE: this file is loaded from both size_table.liquid and style-with.liquid (the
   shop-the-look cards need it too), and a classic script's top-level const/let is shared across
   every <script> tag on the page — a second copy would throw "already declared".
   ------------------------------------------------------------------------------------------------------------------ */
(() => {

// Second copy of this script on the same page (see above): the first one already wired the
// document-level listeners, only guides that appeared since need initialising.
if (window.SizeGuideMemory) {
  window.initSizeGuides?.();
  return;
}

const SELECTORS = {
  guide: '[data-size-guide]',
  matrix: '[data-size-matrix]',
  column: '[data-col]',
  headerCell: 'thead [data-col]',
  // Every row carrying a size system (INT, FR, US...), header included — not the measurement rows
  sizeRow: 'tr:not([data-measurement-row])',
  rowLabel: '.size-guide__matrix-cell--label',
  measurementRow: '[data-measurement-row]',
  measurementsWrapper: '[data-size-guide-measurements]',
  measurementsList: '[data-size-guide-measurements-list]',
  measurementTemplate: '#size-guide-measurement-template',
  measurementValue: '[data-size-guide-measurement-value]',
  measurementLabel: '[data-size-guide-measurement-label]',
  unitSwitch: '[data-unit-switch]',
  removeSize: '[data-size-guide-remove-size]',
  hint: '[data-size-guide-hint]',
  cta: '[data-size-guide-cta]',
  tryon: '[data-size-guide-tryon]',
  variantIdInput: 'input[name="id"]',
  variantCell: '[data-variant-id]'
};

const CLASSES = {
  cell: 'size-guide__matrix-cell',
  hover: 'size-guide__matrix-cell--hover',
  selected: 'size-guide__matrix-cell--selected',
  selectedCell: 'size-guide__matrix-cell--selected-cell',
  ctaLabel: 'size-guide__add-to-cart-label'
};

// dataset key marking a guide as wired; distinct from the data-size-guide selector above
const READY_FLAG = 'sizeGuideReady';

const CM_PER_INCH = 2.54;
const RANGE_SEPARATOR = '-';
const SIZE_LABEL_PLACEHOLDER = '%size%';

// Safety net for closeAfterAdd: if the add never settles, the guide still closes.
const ADD_TIMEOUT_MS = 6000;

const EVENTS = {
  variantAdd: 'variant:add',
  cartError: 'cart:error',
  dialogShow: 'dialog:after-show',
  // Fired on document once a size has been remembered, so the recommendation labels refresh
  remembered: 'size-guide:remembered'
};

// One entry per sizing family: { system, sizes: { INT: 'S', FR: '36', ... } }
const STORAGE_KEY = 'size-guide:sizes';

// Per-guide state, reachable from the document-level add listener (see rememberAddedVariant)
const guideStates = new WeakMap();

// Real per-product measurements come from the product metafield custom.garment_measurements (JSON, keyed
// size -> field -> "cm" value), serialised by Liquid into data-measurements on the guide root.
// Shop-the-look cards render the look product's own guide, so each guide carries its own data.
const parseMeasurements = (raw) => {
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
};

// The matrix's own data-techpack-sizes lists, in column order, the size key that file uses for
// this category (FR "34".."42" for most, INT "XS".."XL" for tops/knitwear, US "24".."32" for
// jeans) — always the same convention the techpack itself grades by for that garment.
// Returns null when the size or field is missing (or blank) in the JSON — never a placeholder.
const getMeasurement = (matrix, row, column, state) => {
  const sizes = matrix.dataset.techpackSizes?.split(',');
  const sizeKey = sizes?.[column];
  const field = row.dataset.field;
  const value = state.measurements?.[sizeKey]?.[field];

  return value == null || String(value).trim() === '' ? null : value;
};

const formatNumber = (value) => value.toFixed(1).replace('.', ',');

const cmToInchesValue = (cmValue) => {
  const number = parseFloat(String(cmValue).replace(',', '.'));

  return Number.isNaN(number) ? cmValue : formatNumber(number / CM_PER_INCH);
};

// Values hold either a single measurement or a "min - max" range
const cmToInches = (cmValue) => {
  const value = String(cmValue).trim();

  if (value.includes(RANGE_SEPARATOR)) {
    const parts = value.split(RANGE_SEPARATOR).map((part) => part.trim());

    if (parts.length === 2) {
      return `${cmToInchesValue(parts[0])} ${RANGE_SEPARATOR} ${cmToInchesValue(parts[1])}`;
    }
  }

  return cmToInchesValue(value);
};

/* ---- Matrix reading ---------------------------------------------------------------------------------------------- */

const getRowSystem = (row) => row.querySelector(SELECTORS.rowLabel)?.textContent.trim() || '';

const getRowValue = (row, column) => row.querySelector(`${SELECTORS.column}[data-col="${column}"]`)?.textContent.trim() || '';

// [{ row, system }] for every size-system row, header first (so rows[0] is the native system).
const getSizeRows = (matrix) => Array.from(matrix.querySelectorAll(SELECTORS.sizeRow))
  .map((row) => ({ row, system: getRowSystem(row) }))
  .filter(({ system }) => system);

const getHeaderLabel = (matrix, column) => matrix.querySelector(`${SELECTORS.headerCell}[data-col="${column}"]`)?.textContent.trim() || '';

/* ---- Remembered size --------------------------------------------------------------------------------------------- */

const readMemory = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch (error) {
    return {};
  }
};

const readFamilySize = (family) => (family ? readMemory()[family] || null : null);

const writeFamilySize = (family, entry) => {
  if (!family) {
    return;
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readMemory(), [family]: entry }));
  } catch (error) {
    // Private mode / blocked storage: the size is simply not remembered
  }
};

// Snapshot of one column: the size in every system this matrix lists, plus the system the shopper
// actually clicked — the row the next guide of the family opens on.
const buildEntry = (matrix, column, system) => {
  const sizes = {};

  getSizeRows(matrix).forEach(({ row, system: rowSystem }) => {
    const value = getRowValue(row, column);

    if (value) {
      sizes[rowSystem] = value;
    }
  });

  return { system, sizes };
};

const findCellByValue = (row, value) => Array.from(row.querySelectorAll(SELECTORS.column))
  .find((cell) => cell.textContent.trim() === value) || null;

// Locates a remembered entry in another matrix. The clicked system is tried first, then every
// other system the entry knows about: a size picked on the US row of the jeans matrix lands on the
// FR row of trousers, since trousers have no US waist sizes.
const resolveEntry = (matrix, entry) => {
  if (!entry?.sizes) {
    return null;
  }

  const rows = getSizeRows(matrix);
  const systems = [entry.system, ...Object.keys(entry.sizes)].filter((system, index, all) => system && all.indexOf(system) === index);

  for (const system of systems) {
    const row = rows.find((candidate) => candidate.system === system)?.row;
    const cell = row && findCellByValue(row, entry.sizes[system]);

    if (cell) {
      const column = cell.dataset.col;

      return {
        cell,
        column,
        system,
        size: getHeaderLabel(matrix, column),
        // The size in the system that matched, and whether that is just the header row again
        // (then the "equivalent" would only repeat the size)
        equivalent: { system, value: entry.sizes[system], native: system === rows[0]?.system }
      };
    }
  }

  return null;
};

/* ---- Hover / selection -------------------------------------------------------------------------------------------- */

const clearColumnClass = (matrix, className) => {
  matrix.querySelectorAll(`.${className}`).forEach((cell) => cell.classList.remove(className));
};

const setColumnClass = (matrix, column, className) => {
  matrix.querySelectorAll(`${SELECTORS.column}[data-col="${column}"]`).forEach((cell) => cell.classList.add(className));
};

const wireCellHover = (guide, matrix) => {
  matrix.addEventListener('mouseover', (event) => {
    const cell = event.target.closest(SELECTORS.column);

    if (!cell) {
      return;
    }

    // Recomputed on every cell entry rather than diffed against the last hover: the table is
    // small enough that this is free, and it sidesteps tracking column/row state separately.
    clearColumnClass(matrix, CLASSES.hover);
    setColumnClass(matrix, cell.dataset.col, CLASSES.hover);
    cell.closest('tr')?.querySelectorAll(`.${CLASSES.cell}`).forEach((rowCell) => rowCell.classList.add(CLASSES.hover));
  });

  matrix.addEventListener('mouseleave', () => {
    clearColumnClass(matrix, CLASSES.hover);
  });
};

// Only fields present in the product's garment_measurements JSON are shown — the placeholder
// data-cm / data-in values in the markup are never used, and the whole panel stays hidden when
// the selected size has no measurement at all.
const buildMeasurementBoxes = (guide, matrix, state) => {
  const wrapper = guide.querySelector(SELECTORS.measurementsWrapper);
  const list = guide.querySelector(SELECTORS.measurementsList);
  const template = guide.querySelector(SELECTORS.measurementTemplate) || document.querySelector(SELECTORS.measurementTemplate);

  if (!wrapper || !list || !template || state.selectedColumn == null) {
    return;
  }

  const boxes = Array.from(matrix.querySelectorAll(SELECTORS.measurementRow)).map((row) => {
    const label = row.querySelector('th')?.textContent.trim();
    const cm = getMeasurement(matrix, row, state.selectedColumn, state);

    if (!label || cm == null) {
      return null;
    }

    const box = template.content.firstElementChild.cloneNode(true);
    box.querySelector(SELECTORS.measurementValue).textContent = state.unit === 'in' ? cmToInches(cm) : cm;
    box.querySelector(SELECTORS.measurementLabel).textContent = label;

    return box;
  }).filter(Boolean);

  list.replaceChildren(...boxes);
  wrapper.hidden = boxes.length === 0;
};

const updateCta = (guide, matrix, state) => {
  const cta = guide.querySelector(SELECTORS.cta);

  if (!cta) {
    return;
  }

  // Which sizing system is the real Shopify option varies by product, not just by type (some
  // dresses sell by EU number, others by letter) — every row that could plausibly be it carries
  // a lookup, so whichever one resolved for this product is used, wherever it lives in the table.
  const nativeCell = matrix.querySelector(`${SELECTORS.column}[data-col="${state.selectedColumn}"]${SELECTORS.variantCell}`);
  const variantId = nativeCell?.dataset.variantId;

  // The chart is shared across the whole product type — this SKU may not stock every column.
  if (!variantId) {
    cta.hidden = true;
    return;
  }

  const available = nativeCell.dataset.variantAvailable !== 'false';
  const sizeLabel = getHeaderLabel(matrix, state.selectedColumn);
  const label = available ? cta.dataset.labelTemplate.replace(SIZE_LABEL_PLACEHOLDER, sizeLabel) : cta.dataset.soldOutLabel;

  // A wrapped span, not plain text, so the busy state (see submitSizeGuideCart) can hide just the
  // label and reveal a spinner via ::after — same convention as the card's own quick-buy button.
  cta.replaceChildren(Object.assign(document.createElement('span'), {
    className: CLASSES.ctaLabel,
    textContent: label
  }));
  cta.disabled = !available;
  cta.dataset.variantId = variantId;
  cta.hidden = false;
};

// The matrix header shows either the "click a size" hint or the "Remove selected size" button
const toggleHeaderControls = (guide, selected) => {
  const remove = guide.querySelector(SELECTORS.removeSize);
  const hint = guide.querySelector(SELECTORS.hint);

  if (remove) {
    remove.hidden = !selected;
  }

  if (hint) {
    hint.hidden = selected;
  }
};

// Only the clicked system's row stays: the shopper reads sizes in one system, the rest is noise
// once they have found theirs. The whole row is kept, so they can still move along it.
const collapseToRow = (guide, matrix, row) => {
  matrix.querySelectorAll(SELECTORS.sizeRow).forEach((candidate) => {
    candidate.hidden = candidate !== row;
  });

  toggleHeaderControls(guide, true);
};

const expandRows = (guide, matrix) => {
  matrix.querySelectorAll(SELECTORS.sizeRow).forEach((row) => {
    row.hidden = false;
  });

  toggleHeaderControls(guide, false);
};

// Back to the untouched guide: every system visible, no column shaded, measurements and CTA gone.
const clearSelection = (guide, matrix, state) => {
  state.selectedColumn = null;
  state.selectedSystem = '';

  clearColumnClass(matrix, CLASSES.selected);
  clearColumnClass(matrix, CLASSES.selectedCell);
  expandRows(guide, matrix);

  const measurements = guide.querySelector(SELECTORS.measurementsWrapper);
  const cta = guide.querySelector(SELECTORS.cta);

  if (measurements) {
    measurements.hidden = true;
  }

  if (cta) {
    cta.hidden = true;
  }
};

const selectColumn = (guide, matrix, state, column, clickedCell) => {
  const row = clickedCell.closest('tr');

  state.selectedColumn = column;
  state.selectedSystem = row ? getRowSystem(row) : '';

  clearColumnClass(matrix, CLASSES.selected);
  setColumnClass(matrix, column, CLASSES.selected);

  // The border marks whichever cell the shopper actually clicked, not a fixed row — the column
  // shading already carries the "you picked this size" meaning, this is just their tap/click.
  clearColumnClass(matrix, CLASSES.selectedCell);
  clickedCell.classList.add(CLASSES.selectedCell);

  if (row) {
    collapseToRow(guide, matrix, row);
  }

  buildMeasurementBoxes(guide, matrix, state);
  updateCta(guide, matrix, state);
};

const wireColumnSelect = (guide, matrix, state) => {
  matrix.addEventListener('click', (event) => {
    const cell = event.target.closest(SELECTORS.column);
    const column = cell?.dataset.col;

    if (column) {
      selectColumn(guide, matrix, state, column, cell);
    }
  });
};

const wireRemoveSize = (guide, matrix, state) => {
  guide.querySelector(SELECTORS.removeSize)?.addEventListener('click', () => clearSelection(guide, matrix, state));
};

const wireUnitSwitch = (guide, matrix, state) => {
  const toggle = guide.querySelector(SELECTORS.unitSwitch);

  if (!toggle) {
    return;
  }

  toggle.addEventListener('change', () => {
    state.unit = toggle.getAttribute('aria-checked') === 'true' ? 'cm' : 'in';
    toggle.setAttribute('aria-checked', state.unit === 'in' ? 'true' : 'false');
    buildMeasurementBoxes(guide, matrix, state);
  });
};

// The guide opens on the shopper's remembered size for this family, collapsed to the system they
// used last time — unless they have already picked something in this guide.
const preselectRememberedSize = (guide, matrix, state) => {
  if (state.selectedColumn != null) {
    return;
  }

  const resolved = resolveEntry(matrix, readFamilySize(guide.dataset.family));

  if (resolved) {
    selectColumn(guide, matrix, state, resolved.column, resolved.cell);
  }
};

const wirePreselect = (guide, matrix, state) => {
  guide.addEventListener(EVENTS.dialogShow, () => preselectRememberedSize(guide, matrix, state));
};

/* ---- Add to cart -------------------------------------------------------------------------------------------------- */

// Left open until the add lands, so the drawer does not close before the confirmation arrives.
const closeAfterAdd = (guide, form, cta) => {
  let closed = false;
  const finish = () => {
    if (closed) {
      return;
    }

    closed = true;
    cta.disabled = false;
    cta.removeAttribute('aria-busy');
    guide.hide?.();
  };

  form.addEventListener(EVENTS.variantAdd, finish, { once: true });
  form.addEventListener(EVENTS.cartError, finish, { once: true });
  setTimeout(finish, ADD_TIMEOUT_MS);
};

const submitSizeGuideCart = (cta, guide) => {
  const form = document.getElementById(cta.dataset.formId);
  const idInput = form?.querySelector(SELECTORS.variantIdInput);

  if (!idInput) {
    return;
  }

  idInput.value = cta.dataset.variantId;
  // The add itself takes a couple of seconds — aria-busy swaps the label for a spinner (see
  // custom.css) so the tap reads as registered instead of dead, same convention as the card's
  // own quick-buy button.
  cta.disabled = true;
  cta.setAttribute('aria-busy', 'true');
  form.requestSubmit();
  closeAfterAdd(guide, form, cta);
};

const wireCta = (guide) => {
  const cta = guide.querySelector(SELECTORS.cta);

  cta?.addEventListener('click', () => submitSizeGuideCart(cta, guide));
};

// Every guide on the page that knows this variant (a product's guide is rendered twice, drawer
// and popover), paired with its state. The one the shopper picked that very column in comes
// first — its clicked system is the one worth remembering.
const findGuidesForVariant = (variantId) => Array.from(document.querySelectorAll(`${SELECTORS.guide} ${SELECTORS.variantCell}[data-variant-id="${variantId}"]`))
  .map((cell) => ({ cell, guide: cell.closest(SELECTORS.guide), state: guideStates.get(cell.closest(SELECTORS.guide)) }))
  .sort((a, b) => Number(b.state?.selectedColumn === b.cell.dataset.col) - Number(a.state?.selectedColumn === a.cell.dataset.col));

// Whatever added the variant — this guide's CTA, the product form, a shop-the-look card — the
// matrix of the guide that knows that variant tells which column it is. The system remembered is
// the one the shopper clicked in that guide when it matches, else the guide's native header row.
const rememberAddedVariant = (variantId) => {
  const match = findGuidesForVariant(variantId)[0];
  const matrix = match?.guide.querySelector(SELECTORS.matrix);
  const family = match?.guide.dataset.family;

  if (!matrix || !family) {
    return;
  }

  const { cell, state } = match;
  const column = cell.dataset.col;
  const header = matrix.querySelector(SELECTORS.sizeRow);
  const system = (state?.selectedColumn === column && state.selectedSystem) || (header ? getRowSystem(header) : '');

  writeFamilySize(family, buildEntry(matrix, column, system));
  document.dispatchEvent(new CustomEvent(EVENTS.remembered, { detail: { family } }));
};

// The theme's product-form dispatches variant:add (bubbling) with the /cart/add.js items.
const wireAddListener = () => {
  document.addEventListener(EVENTS.variantAdd, (event) => {
    const variantId = event.detail?.items?.[0]?.id ?? event.detail?.item?.id;

    if (variantId != null) {
      rememberAddedVariant(String(variantId));
    }
  }, true);
};

/* ---- Try-on ------------------------------------------------------------------------------------------------------ */

// The DressX try-on widget loads globally (see layout/theme.liquid) and has no documented entry
// point in this repo — same situation Kleep was in before component-kleep-cta.js. Best-effort
// hook for whichever global the vendor script turns out to expose; a no-op until confirmed live.
const openTryOn = () => {
  const opener = window.DressX?.open || window.dressXOpen;

  if (typeof opener === 'function') {
    opener();
  }
};

const wireTryon = (guide) => {
  guide.querySelector(SELECTORS.tryon)?.addEventListener('click', openTryOn);
};

/* ---- Init -------------------------------------------------------------------------------------------------------- */

const initGuide = (guide) => {
  if (guide.dataset[READY_FLAG]) {
    return;
  }

  guide.dataset[READY_FLAG] = 'true';

  const matrix = guide.querySelector(SELECTORS.matrix);
  const state = { selectedColumn: null, selectedSystem: '', unit: 'cm', measurements: null };

  guideStates.set(guide, state);

  if (matrix) {
    wireCellHover(guide, matrix);
    wireColumnSelect(guide, matrix, state);
    wireRemoveSize(guide, matrix, state);
    wireUnitSwitch(guide, matrix, state);
    wirePreselect(guide, matrix, state);

    // Missing metafield, size or field means that measurement is simply not shown.
    state.measurements = parseMeasurements(guide.dataset.measurements);
  }

  wireCta(guide);
  wireTryon(guide);
};

const initSizeGuides = (root = document) => {
  root.querySelectorAll(SELECTORS.guide).forEach(initGuide);
};

window.initSizeGuides = initSizeGuides;
window.SizeGuideMemory = { readFamilySize, resolveEntry, EVENTS: { remembered: EVENTS.remembered } };

wireAddListener();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initSizeGuides());
} else {
  initSizeGuides();
}

})();
